import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import classNames from "classnames";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { GUEST_DEFAULT_NAME, UNKNOWN_CLIENT_ID } from "../Constants";
import { canvasSelectionAtom, userAtom } from "../SharedIframeState";
import { ActionType } from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import {
  DropdownMenuItem,
  HeaderMenuClassName,
  MenuItemClassName,
} from "../ui/Menu";
import { canvasDispatch } from "./CanvasDispatch";
import { ParentComlinkAPI } from "./FrameSandboxAPI";
import { presenceState } from "./Presence";
import { applicationState } from "./Sync";

function RenameGuestButton() {
  return (
    <button
      onClick={() => {
        const name = window.prompt("What's your name?");
        if (name !== null && name.trim().length > 0) {
          canvasDispatch([ActionType.PresenceName, name.trim()]);
        }
      }}
      className="ml-1 flex p-0.5 text-neutral-300 hover:text-neutral-500 focus:text-neutral-500 transition"
    >
      <FeatherIcon icon="edit-2" size="sm" />
    </button>
  );
}

const PresenceList = observer(({ canvasId }: { canvasId: string }) => {
  const myPresenceClientId = applicationState.clientId ?? UNKNOWN_CLIENT_ID;
  const myPresence = presenceState[myPresenceClientId];
  return (
    <div className="font-medium text-neutral-600">
      {myPresence !== undefined ? (
        <div className={MenuItemClassName}>
          <div
            className="w-2 h-2 rounded-full mr-2"
            style={{ backgroundColor: `#${myPresence.color}` }}
          />
          <div>
            {myPresence.name ?? GUEST_DEFAULT_NAME}{" "}
            <span className="text-neutral-300">(you)</span>
          </div>
          <div className="flex-grow" />
          {userAtom.value === undefined ? <RenameGuestButton /> : null}
        </div>
      ) : null}
      {Object.entries(presenceState).map(([clientId, userPresence]) =>
        clientId !== `${myPresenceClientId}` ? (
          <div className={MenuItemClassName} key={clientId}>
            <div className="flex gap-2 items-center">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: `#${userPresence.color}` }}
              />
              <div>{userPresence.name ?? GUEST_DEFAULT_NAME}</div>
            </div>
          </div>
        ) : null
      )}
      {canvasSelectionAtom.value?.[0] === "user" ? (
        <DropdownMenuItem
          onClick={() => {
            ParentComlinkAPI.showInvitesDialog(canvasId);
          }}
          className="gap-2 font-medium"
        >
          manage invites
        </DropdownMenuItem>
      ) : null}
    </div>
  );
});

export const HeaderPresenceButton = observer(
  ({ canvasId }: { canvasId: string }) => {
    const [open, setOpen] = useState(() => {
      const myPresenceClientId = applicationState.clientId ?? UNKNOWN_CLIENT_ID;
      // open if user is anonymous guest
      return (
        presenceState[myPresenceClientId] !== undefined &&
        presenceState[myPresenceClientId].name === undefined
      );
    });
    const numPresenceComputed = computed(
      () => Object.keys(presenceState).length
    );
    const numPresence = numPresenceComputed.get();

    return (
      <DropdownMenuPrimitive.Root
        open={open}
        onOpenChange={setOpen}
        modal={false}
      >
        <DropdownMenuPrimitive.Trigger asChild={true}>
          <button
            className={classNames(
              "px-2 text-neutral-600 flex items-center pointer-events-auto group",
              open ? "bg-secondary-100" : "hover:bg-neutral-50"
            )}
          >
            <div className="flex gap-2 items-center">
              <FeatherIcon icon="users" size="md" />
              {numPresence > 1 ? numPresence : null}
            </div>
          </button>
        </DropdownMenuPrimitive.Trigger>
        <DropdownMenuPrimitive.Content
          align="end"
          className={HeaderMenuClassName}
        >
          <PresenceList canvasId={canvasId} />
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Root>
    );
  }
);
